import React, { useEffect } from 'react';
import { Button, Drawer, Form, Input, InputNumber, message, Select, Space } from 'antd';
import { useSnapshot } from 'valtio';
import { changeGrowthValueApi } from '@/api/member/member';
import { state } from '@/store';

const changeTypeList = [
  { label: '增加', value: 'ADD' },
  { label: '减少', value: 'SUB' },
];

interface Props {
  open: boolean;
  onClose: () => void;
  oneId: string;
  mobile?: string;
  memberCardId?: number;
  shopId?: string | number;
  channelType?: string;
  platformId?: string;
  uniqueId?: string;
  currentGrowthValue?: number;
  onSuccess?: () => void;
}

const ChangeGrowthDrawer: React.FC<Props> = ({
  open,
  onClose,
  oneId,
  mobile,
  memberCardId,
  shopId,
  channelType,
  platformId,
  uniqueId,
  currentGrowthValue,
  onSuccess,
}) => {
  const [form] = Form.useForm();
  const snap = useSnapshot(state);
  const changeType = Form.useWatch('changeType', form);

  const memberCardValue =
    shopId !== undefined && shopId !== null
      ? String(shopId)
      : snap.currentShopId
        ? String(snap.currentShopId)
        : undefined;

  useEffect(() => {
    if (!open) return;
    form.resetFields();
    form.setFieldsValue({
      oneId,
      mobile: mobile || '-',
      memberCard: memberCardValue,
      currentGrowth: typeof currentGrowthValue === 'number' ? currentGrowthValue : '-',
      changeType: undefined,
      changeQuantity: undefined,
      remark: '',
    });
  }, [open, oneId, mobile, memberCardValue, currentGrowthValue, form]);

  const handleSubmit = async () => {
    try {
      const values = await form.validateFields();
      if (!memberCardId) {
        message.warning('缺少会员卡信息');
        return;
      }
      const res = await changeGrowthValueApi({
        channelType: channelType || '',
        shopId: memberCardValue || '',
        memberCardId,
        oneId,
        uniqueId: uniqueId || '',
        phone: mobile || '',
        platformId: platformId || '',
        orderId: '',
        refundId: '',
        changeType: values.changeType,
        changeQuantity: Number(values.changeQuantity),
        changeCause: '手动变更',
        remark: values.remark,
      });
      if (res?.code === 200) {
        message.success('成长值调整成功');
        onClose();
        onSuccess?.();
      }
    } catch (error: any) {
      if (error?.errorFields) return;
    }
  };

  return (
    <Drawer
      destroyOnHidden
      open={open}
      title="成长值变更"
      width={420}
      onClose={onClose}
      extra={
        <Space>
          <Button onClick={onClose}>取消</Button>
          <Button
            type="primary"
            onClick={handleSubmit}>
            确定
          </Button>
        </Space>
      }>
      <Form
        form={form}
        layout="vertical">
        <Form.Item
          label="OneID"
          name="oneId">
          <Input disabled />
        </Form.Item>
        <Form.Item
          label="会员手机号"
          name="mobile">
          <Input disabled />
        </Form.Item>
        <Form.Item
          label="会员卡"
          name="memberCard">
          <Select
            disabled
            options={snap.shopList?.map((item: any) => ({ label: item.shopName, value: String(item.shopId) }))}
          />
        </Form.Item>
        <Form.Item
          label="当前成长值"
          name="currentGrowth">
          <Input disabled />
        </Form.Item>
        <Form.Item
          label="变更类型"
          required>
          <Space
            size={12}
            style={{ width: '100%' }}>
            <Form.Item
              name="changeType"
              rules={[{ required: true, message: '请选择变更类型' }]}
              noStyle>
              <Select
                placeholder="请选择"
                options={changeTypeList}
                style={{ width: 100 }}
              />
            </Form.Item>
            <Form.Item
              name="changeQuantity"
              rules={[
                { required: true, message: '请输入成长值' },
                () => ({
                  validator(_, value) {
                    if (!value || Number(value) > 0) {
                      return Promise.resolve();
                    }
                    return Promise.reject(new Error('请输入大于 0 的成长值'));
                  },
                }),
              ]}
              noStyle>
              <InputNumber
                min={1}
                precision={0}
                placeholder="请输入"
                style={{ width: 258 }}
                disabled={!changeType}
              />
            </Form.Item>
          </Space>
        </Form.Item>
        <Form.Item
          label="备注"
          name="remark"
          rules={[{ required: true, message: '请输入备注' }]}>
          <Input.TextArea
            rows={3}
            maxLength={100}
            showCount
            placeholder="请输入备注"
          />
        </Form.Item>
      </Form>
    </Drawer>
  );
};

export default ChangeGrowthDrawer;
